import { type JSX, splitProps } from 'solid-js'
import { cn } from '../lib/cn.ts'
import { tv, type VariantProps } from '../lib/tv.ts'

/**
 * Row rhythm and header treatment live here rather than in each screen, so every table in the
 * system reads the same. `[&_tbody_tr]` selectors keep call sites writing plain <tr>/<td>.
 *
 * Console conventions this encodes:
 *   · the header is a hairline strip, not a filled band — a heavy header competes with the data
 *   · rows are 33px, which is what makes a stock list scannable rather than scrollable
 *   · `[&_tbody_td]:tabular-nums` so a numeric column lines up even when a cell holds raw text
 *   · the header sticks, because a production table that outgrows the viewport is the normal case
 *
 * `flush` drops the border and radius, for a table that is the whole body of a Panel and would
 * otherwise draw a second box inside the first.
 */
const table = tv({
  slots: {
    wrapper: 'overflow-x-auto',
    table: [
      'w-full border-collapse text-left text-body text-text',
      '[&_thead_th]:sticky [&_thead_th]:top-0 [&_thead_th]:z-1',
      '[&_thead_th]:border-b [&_thead_th]:border-border [&_thead_th]:bg-surface',
      '[&_thead_th]:px-cell-x [&_thead_th]:py-cell-y',
      '[&_thead_th]:text-micro [&_thead_th]:font-semibold',
      '[&_thead_th]:text-text-subtle [&_thead_th]:uppercase',
      '[&_tbody_tr]:border-b [&_tbody_tr]:border-border/70',
      '[&_tbody_tr:last-child]:border-b-0',
      '[&_tbody_tr]:transition-colors [&_tbody_tr]:duration-(--duration-press)',
      '[&_tbody_tr:hover]:bg-surface-muted',
      '[&_tbody_td]:px-cell-x [&_tbody_td]:py-cell-y [&_tbody_td]:tabular-nums',
    ],
  },
  variants: {
    flush: {
      false: { wrapper: 'rounded-card border border-border bg-surface' },
      true: {},
    },
  },
  defaultVariants: { flush: false },
})

export type TableProps = JSX.HTMLAttributes<HTMLTableElement> & VariantProps<typeof table>

export const Table = (props: TableProps) => {
  const [local, rest] = splitProps(props, ['flush', 'class'])
  const styles = () => table({ flush: local.flush })

  return (
    <div class={styles().wrapper()}>
      <table {...rest} class={cn(styles().table(), local.class)} />
    </div>
  )
}
